import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  CalendarDays,
  CalendarRange,
  ClipboardList,
  Download,
  FileSpreadsheet,
  FileText,
  RotateCcw,
  Search,
  TrendingDown,
} from "lucide-react";
import AppShell from "../components/AppShell";
import StatCard from "../components/StatCard";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import api, { downloadFile } from "../lib/api";
import { getErrorMessage } from "../lib/formatters";

const companyNav = [
  { path: "/company/dashboard", label: "Company Dashboard", shortLabel: "HOME", testId: "company-dashboard" },
  { path: "/company/employees", label: "Employees", shortLabel: "PEOPLE", testId: "company-employees" },
  { path: "/company/attendance", label: "Attendance Monitor", shortLabel: "TRACK", testId: "company-attendance" },
  { path: "/company/approvals", label: "Approvals", shortLabel: "QUEUE", testId: "company-approvals" },
  { path: "/company/kiosk", label: "Kiosk QR", shortLabel: "QR", testId: "company-kiosk" },
  { path: "/company/leave-settings", label: "Leave Settings", shortLabel: "LEAVE", testId: "company-leave-settings" },
  { path: "/company/reports", label: "Reports", shortLabel: "REPORTS", testId: "company-reports" },
  { path: "/company/settings", label: "Settings", shortLabel: "CONFIG", testId: "company-settings" },
];

const managerNav = [
  { path: "/manager/dashboard", label: "Manager Dashboard", shortLabel: "HOME", testId: "manager-dashboard" },
  { path: "/manager/team", label: "Team Attendance", shortLabel: "TEAM", testId: "manager-team" },
  { path: "/manager/approvals", label: "Approvals", shortLabel: "QUEUE", testId: "manager-approvals" },
  { path: "/manager/reports", label: "Reports", shortLabel: "REPORTS", testId: "manager-reports" },
];

const iconMap = { CalendarDays, CalendarRange, TrendingDown, ClipboardList };

const REPORT_META = {
  "daily-attendance": { icon: "CalendarDays", accent: "#F4A300", control: "date" },
  "monthly-attendance": { icon: "CalendarRange", accent: "#0D1B2A", control: "month" },
  lop: { icon: "TrendingDown", accent: "#B91C1C", control: "range" },
  "muster-roll": { icon: "ClipboardList", accent: "#0D7A5F", control: "month" },
};

const DAILY_STATUS_OPTIONS = ["Present", "Late", "Absent", "Leave", "Half Day", "Missing Punch", "Holiday", "Week-Off"];

const todayIso = () => new Date().toLocaleDateString("en-CA");
const currentMonth = () => todayIso().slice(0, 7);
const firstOfMonthIso = () => `${currentMonth()}-01`;

const codeStyles = {
  P: "bg-[#F4A300]/15 text-[#B4780A] border-[#F4A300]/30",
  A: "bg-rose-50 text-rose-700 border-rose-200",
  L: "bg-[#0D1B2A]/8 text-[#0D1B2A] border-[#0D1B2A]/15",
  H: "bg-emerald-50 text-emerald-700 border-emerald-200",
  WO: "bg-slate-100 text-slate-500 border-slate-200",
  "-": "bg-white text-slate-300 border-slate-100",
};

function ReportCard({ report, onOpen }) {
  const meta = REPORT_META[report.id] || {};
  const Icon = iconMap[report.icon] || CalendarDays;
  return (
    <Card
      className="group flex flex-col justify-between border-[#0D1B2A]/10 bg-white transition duration-200 hover:-translate-y-1 hover:border-[#F4A300]/40 hover:shadow-[0_24px_60px_rgba(13,27,42,0.12)]"
      data-testid={`report-card-${report.id}`}
    >
      <CardHeader className="space-y-4">
        <div className="flex items-center gap-3">
          <span
            className="flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-sm"
            style={{ backgroundColor: meta.accent || "#0D1B2A" }}
          >
            <Icon className="h-6 w-6" />
          </span>
          <CardTitle className="text-lg font-bold text-[#0D1B2A]" data-testid={`report-card-title-${report.id}`}>
            {report.name}
          </CardTitle>
        </div>
        <CardDescription className="text-sm text-[#0D1B2A]/60" data-testid={`report-card-desc-${report.id}`}>
          {report.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex items-center justify-between pt-0">
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0D1B2A]/40">
          {meta.control === "date" ? "By date" : meta.control === "month" ? "Monthly" : "Date range"}
        </span>
        <Button
          type="button"
          className="rounded-full bg-[#0D1B2A] px-5 text-white hover:bg-[#1a2c40]"
          onClick={() => onOpen(report)}
          data-testid={`report-open-${report.id}`}
        >
          View Report
        </Button>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const { user, tenant, logout } = useAuth();
  const navItems = user?.role === "manager" ? managerNav : companyNav;

  const [catalog, setCatalog] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ departments: [], teams: [], business_units: [], locations: [], employees: [] });
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [expandedLop, setExpandedLop] = useState(() => new Set());
  const [filters, setFilters] = useState({
    date: todayIso(),
    month: currentMonth(),
    start_date: firstOfMonthIso(),
    end_date: todayIso(),
    employee_id: "",
    department_id: "",
    team_id: "",
    business_unit_id: "",
    location_id: "",
    attendance_status: "",
  });

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const [catalogRes, filtersRes] = await Promise.all([api.get("/reports/catalog"), api.get("/reports/filters")]);
        setCatalog(catalogRes.data.reports);
        setFilterOptions(filtersRes.data);
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };
    bootstrap();
  }, []);

  const buildParams = useCallback(
    (report) => {
      const control = REPORT_META[report.id]?.control;
      const params = {
        employee_id: filters.employee_id || undefined,
        department_id: filters.department_id || undefined,
        team_id: filters.team_id || undefined,
        business_unit_id: filters.business_unit_id || undefined,
        location_id: filters.location_id || undefined,
      };
      if (control === "date") {
        params.date = filters.date;
        if (report.id === "daily-attendance" && filters.attendance_status) params.attendance_status = filters.attendance_status;
      } else if (control === "month") {
        params.month = filters.month;
      } else if (control === "range") {
        params.start_date = filters.start_date;
        params.end_date = filters.end_date;
      }
      return params;
    },
    [filters],
  );

  const loadReport = useCallback(
    async (report) => {
      setLoading(true);
      try {
        const response = await api.get(`/reports/${report.id}`, { params: buildParams(report) });
        setReportData(response.data);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [buildParams],
  );

  const openReport = (report) => {
    setActiveReport(report);
    setReportData(null);
    setExpandedLop(new Set());
    loadReport(report);
  };

  const closeReport = () => {
    setActiveReport(null);
    setReportData(null);
  };

  const resetFilters = () => {
    setFilters({
      date: todayIso(),
      month: currentMonth(),
      start_date: firstOfMonthIso(),
      end_date: todayIso(),
      employee_id: "",
      department_id: "",
      team_id: "",
      business_unit_id: "",
      location_id: "",
      attendance_status: "",
    });
  };

  const exportReport = async (fmt) => {
    if (!activeReport) return;
    setExporting(fmt);
    try {
      await downloadFile(`/reports/${activeReport.id}/export`, { ...buildParams(activeReport), fmt });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setExporting("");
    }
  };

  const toggleLopRow = (index) => {
    setExpandedLop((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const teamsForDepartment = useMemo(() => {
    if (!filters.department_id) return filterOptions.teams;
    return filterOptions.teams.filter((team) => team.department_id === filters.department_id);
  }, [filterOptions.teams, filters.department_id]);

  const selectClass = "h-11 w-full rounded-2xl border border-[#0D1B2A]/15 bg-white px-3 text-sm text-[#0D1B2A] focus:border-[#F4A300] focus:outline-none";

  const renderCommonFilters = () => (
    <>
      <div className="space-y-1">
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D1B2A]/50">Employee</label>
        <select className={selectClass} value={filters.employee_id} onChange={(event) => setFilters((current) => ({ ...current, employee_id: event.target.value }))} data-testid="report-filter-employee">
          <option value="">All employees</option>
          {filterOptions.employees.map((employee) => (
            <option key={employee.id} value={employee.id}>{employee.name}{employee.employee_code ? ` (${employee.employee_code})` : ""}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D1B2A]/50">Department</label>
        <select className={selectClass} value={filters.department_id} onChange={(event) => setFilters((current) => ({ ...current, department_id: event.target.value, team_id: "" }))} data-testid="report-filter-department">
          <option value="">All departments</option>
          {filterOptions.departments.map((department) => (
            <option key={department.id} value={department.id}>{department.name}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D1B2A]/50">Team</label>
        <select className={selectClass} value={filters.team_id} onChange={(event) => setFilters((current) => ({ ...current, team_id: event.target.value }))} data-testid="report-filter-team">
          <option value="">All teams</option>
          {teamsForDepartment.map((team) => (
            <option key={team.id} value={team.id}>{team.name}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D1B2A]/50">Business Unit</label>
        <select className={selectClass} value={filters.business_unit_id} onChange={(event) => setFilters((current) => ({ ...current, business_unit_id: event.target.value }))} data-testid="report-filter-business-unit">
          <option value="">All business units</option>
          {filterOptions.business_units.map((unit) => (
            <option key={unit.id} value={unit.id}>{unit.name}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D1B2A]/50">Location</label>
        <select className={selectClass} value={filters.location_id} onChange={(event) => setFilters((current) => ({ ...current, location_id: event.target.value }))} data-testid="report-filter-location">
          <option value="">All locations</option>
          {filterOptions.locations.map((location) => (
            <option key={location.id} value={location.id}>{location.name}</option>
          ))}
        </select>
      </div>
    </>
  );

  const renderDateControls = () => {
    const control = REPORT_META[activeReport.id]?.control;
    if (control === "date") {
      return (
        <>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D1B2A]/50">Date</label>
            <input type="date" className={selectClass} value={filters.date} onChange={(event) => setFilters((current) => ({ ...current, date: event.target.value }))} data-testid="report-filter-date" />
          </div>
          {activeReport.id === "daily-attendance" ? (
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D1B2A]/50">Attendance Status</label>
              <select className={selectClass} value={filters.attendance_status} onChange={(event) => setFilters((current) => ({ ...current, attendance_status: event.target.value }))} data-testid="report-filter-status">
                <option value="">All statuses</option>
                {DAILY_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          ) : null}
        </>
      );
    }
    if (control === "month") {
      return (
        <div className="space-y-1">
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D1B2A]/50">Month</label>
          <input type="month" className={selectClass} value={filters.month} onChange={(event) => setFilters((current) => ({ ...current, month: event.target.value }))} data-testid="report-filter-month" />
        </div>
      );
    }
    return (
      <>
        <div className="space-y-1">
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D1B2A]/50">Start Date</label>
          <input type="date" className={selectClass} value={filters.start_date} onChange={(event) => setFilters((current) => ({ ...current, start_date: event.target.value }))} data-testid="report-filter-start-date" />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0D1B2A]/50">End Date</label>
          <input type="date" className={selectClass} value={filters.end_date} onChange={(event) => setFilters((current) => ({ ...current, end_date: event.target.value }))} data-testid="report-filter-end-date" />
        </div>
      </>
    );
  };

  const renderLanding = () => (
    <div className="space-y-6">
      <div className="grid gap-5 md:grid-cols-2" data-testid="reports-catalog-grid">
        {catalog.map((report) => (
          <ReportCard key={report.id} report={report} onOpen={openReport} />
        ))}
      </div>
    </div>
  );

  const renderSummaryCards = () => {
    if (!reportData?.summary) return null;
    const summary = reportData.summary;
    let cards = [];
    if (activeReport.id === "daily-attendance") {
      cards = [
        { label: "Total Employees", value: summary.total_employees, accent: "#0D1B2A" },
        { label: "Present", value: summary.present, accent: "#F4A300" },
        { label: "Absent", value: summary.absent, accent: "#B91C1C" },
        { label: "Late", value: summary.late, accent: "#D97706" },
        { label: "On Leave", value: summary.on_leave, accent: "#0D1B2A" },
        { label: "Total Working Hours", value: summary.total_working_hours, accent: "#0D7A5F" },
      ];
    } else if (activeReport.id === "monthly-attendance") {
      cards = [
        { label: "Total Employees", value: summary.total_employees, accent: "#0D1B2A" },
        { label: "Total Working Days", value: summary.total_working_days, accent: "#F4A300" },
        { label: "Total Hours Worked", value: summary.total_hours_worked, accent: "#0D7A5F" },
        { label: "Total Overtime (min)", value: summary.total_overtime_minutes, accent: "#D97706" },
      ];
    } else if (activeReport.id === "lop") {
      cards = [
        { label: "Employees with LOP", value: summary.employees_with_lop, accent: "#B91C1C" },
        { label: "Total LOP Days", value: summary.total_lop_days, accent: "#D97706" },
      ];
    } else if (activeReport.id === "muster-roll") {
      cards = [
        { label: "Total Employees", value: summary.total_employees, accent: "#0D1B2A" },
        { label: "Present Days", value: summary.present_days, accent: "#F4A300" },
        { label: "Absent Days", value: summary.absent_days, accent: "#B91C1C" },
        { label: "Leave Days", value: summary.leave_days, accent: "#0D1B2A" },
        { label: "Holiday Days", value: summary.holiday_days, accent: "#0D7A5F" },
        { label: "Week-Off Days", value: summary.week_off_days, accent: "#64748B" },
      ];
    }
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="report-summary-grid">
        {cards.map((card) => (
          <StatCard key={card.label} label={card.label} value={card.value} accent={card.accent} helper="" testId={`report-stat-${card.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
        ))}
      </div>
    );
  };

  const renderDaily = () => (
    <Card className="border-[#0D1B2A]/10 bg-white" data-testid="daily-report-table-card">
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#0D1B2A]/[0.03]">
              <TableHead>Employee ID</TableHead>
              <TableHead>Employee Name</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Business Unit</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>First Check-In</TableHead>
              <TableHead>Last Check-Out</TableHead>
              <TableHead>Total Hours</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(reportData?.rows || []).map((row, index) => (
              <TableRow key={`${row.employee_id}-${index}`} data-testid={`daily-report-row-${index}`}>
                <TableCell className="font-semibold text-[#0D1B2A]">{row.employee_id}</TableCell>
                <TableCell>{row.employee_name}</TableCell>
                <TableCell>{row.department}</TableCell>
                <TableCell>{row.business_unit}</TableCell>
                <TableCell>{row.location}</TableCell>
                <TableCell>{row.date}</TableCell>
                <TableCell>{row.first_check_in}</TableCell>
                <TableCell className={row.last_check_out === "Missing" ? "font-semibold text-rose-600" : ""}>{row.last_check_out}</TableCell>
                <TableCell>{row.total_working_hours}</TableCell>
                <TableCell><StatusBadge value={row.attendance_status} testId={`daily-report-status-${index}`} /></TableCell>
              </TableRow>
            ))}
            {!reportData?.rows?.length ? (
              <TableRow><TableCell colSpan={10} className="py-10 text-center text-sm text-[#0D1B2A]/50">No attendance rows for this selection.</TableCell></TableRow>
            ) : null}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  const renderMonthly = () => {
    const dates = reportData?.dates || [];
    return (
      <Card className="border-[#0D1B2A]/10 bg-white" data-testid="monthly-report-matrix-card">
        <CardContent className="overflow-x-auto p-0">
          <table className="min-w-full border-collapse text-xs">
            <thead>
              <tr>
                <th rowSpan={2} className="sticky left-0 z-20 min-w-[180px] border-b border-r border-[#0D1B2A]/10 bg-[#0D1B2A] px-3 py-2 text-left text-white">Employee</th>
                {dates.map((day) => (
                  <th key={day.date} colSpan={3} className={`border-b border-l border-[#0D1B2A]/10 px-2 py-1 text-center font-semibold ${day.is_holiday ? "bg-emerald-100 text-emerald-800" : day.is_week_off ? "bg-slate-200 text-slate-600" : "bg-[#0D1B2A]/[0.04] text-[#0D1B2A]"}`} title={day.holiday_name || (day.is_week_off ? "Week-off" : "")}>
                    {day.day}<span className="ml-1 font-normal text-[10px] opacity-60">{day.weekday}</span>
                  </th>
                ))}
              </tr>
              <tr>
                {dates.map((day) => (
                  ["IN", "OUT", "HRS"].map((sub) => (
                    <th key={`${day.date}-${sub}`} className="border-b border-l border-[#0D1B2A]/10 bg-[#F4A300]/15 px-1 py-1 text-center text-[10px] font-semibold text-[#B4780A]">{sub}</th>
                  ))
                ))}
              </tr>
            </thead>
            <tbody>
              {(reportData?.employees || []).map((employee, rowIndex) => (
                <tr key={employee.employee_ref_id} data-testid={`monthly-report-row-${rowIndex}`}>
                  <td className="sticky left-0 z-10 min-w-[180px] border-b border-r border-[#0D1B2A]/10 bg-white px-3 py-2 font-semibold text-[#0D1B2A]">
                    {employee.employee_name}
                    <span className="block text-[10px] font-normal text-[#0D1B2A]/45">{employee.employee_id}</span>
                  </td>
                  {employee.days.map((day) => (
                    ["check_in", "check_out", "total_hours"].map((field, subIndex) => (
                      <td key={`${day.date}-${field}`} className={`border-b border-l border-[#0D1B2A]/10 px-1 py-1 text-center ${subIndex === 2 ? "font-semibold text-[#0D1B2A]" : "text-[#0D1B2A]/70"} ${day.check_out === "Missing" && subIndex === 1 ? "text-rose-600" : ""}`}>
                        {field === "total_hours" ? (day.total_hours || "") : (day[field] || (day.status === "Week-Off" || day.status === "Holiday" ? "—" : ""))}
                      </td>
                    ))
                  ))}
                </tr>
              ))}
              {!reportData?.employees?.length ? (
                <tr><td colSpan={1 + dates.length * 3} className="py-10 text-center text-sm text-[#0D1B2A]/50">No employees for this selection.</td></tr>
              ) : null}
            </tbody>
          </table>
        </CardContent>
      </Card>
    );
  };

  const renderLop = () => (
    <Card className="border-[#0D1B2A]/10 bg-white" data-testid="lop-report-table-card">
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#0D1B2A]/[0.03]">
              <TableHead>Employee ID</TableHead>
              <TableHead>Employee Name</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Business Unit</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Date Range</TableHead>
              <TableHead>LOP Days</TableHead>
              <TableHead>Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(reportData?.rows || []).map((row, index) => (
              <Fragment key={`${row.employee_id}-${index}`}>
                <TableRow data-testid={`lop-report-row-${index}`}>
                  <TableCell className="font-semibold text-[#0D1B2A]">{row.employee_id}</TableCell>
                  <TableCell>{row.employee_name}</TableCell>
                  <TableCell>{row.department}</TableCell>
                  <TableCell>{row.business_unit}</TableCell>
                  <TableCell>{row.location}</TableCell>
                  <TableCell>{row.date_range}</TableCell>
                  <TableCell><span className={`font-bold ${row.lop_days > 0 ? "text-rose-600" : "text-[#0D1B2A]/40"}`}>{row.lop_days}</span></TableCell>
                  <TableCell>
                    {row.lop_days > 0 ? (
                      <Button type="button" variant="outline" className="h-8 rounded-full px-3 text-xs" onClick={() => toggleLopRow(index)} data-testid={`lop-toggle-${index}`}>
                        {expandedLop.has(index) ? "Hide dates" : "View dates"}
                      </Button>
                    ) : <span className="text-xs text-[#0D1B2A]/40">—</span>}
                  </TableCell>
                </TableRow>
                {expandedLop.has(index) && row.lop_days > 0 ? (
                  <TableRow key={`${row.employee_id}-${index}-detail`} className="bg-rose-50/40">
                    <TableCell colSpan={8} data-testid={`lop-detail-${index}`}>
                      <div className="flex flex-wrap gap-2">
                        {row.lop_dates.map((lopDate) => (
                          <span key={lopDate} className="rounded-full border border-rose-200 bg-white px-3 py-1 text-xs font-semibold text-rose-700">{lopDate}</span>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            ))}
            {!reportData?.rows?.length ? (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-[#0D1B2A]/50">No employees for this selection.</TableCell></TableRow>
            ) : null}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  const renderMuster = () => {
    const dates = reportData?.dates || [];
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-3 text-xs font-semibold text-[#0D1B2A]/70" data-testid="muster-legend">
          {[["P", "Present"], ["A", "Absent"], ["L", "Leave"], ["H", "Holiday"], ["WO", "Week-Off"]].map(([code, label]) => (
            <span key={code} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 ${codeStyles[code]}`}>{code} <span className="font-normal opacity-70">{label}</span></span>
          ))}
        </div>
        <Card className="border-[#0D1B2A]/10 bg-white" data-testid="muster-report-matrix-card">
          <CardContent className="overflow-x-auto p-0">
            <table className="min-w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 z-20 min-w-[170px] border-b border-r border-[#0D1B2A]/10 bg-[#0D1B2A] px-3 py-2 text-left text-white">Employee</th>
                  {["Present", "Absent", "Leave", "Holiday", "Week-Off"].map((label) => (
                    <th key={label} className="border-b border-l border-[#0D1B2A]/10 bg-[#F4A300]/15 px-2 py-2 text-center font-semibold text-[#B4780A]">{label}</th>
                  ))}
                  {dates.map((day) => (
                    <th key={day.date} className={`border-b border-l border-[#0D1B2A]/10 px-1 py-2 text-center font-semibold ${day.is_holiday ? "bg-emerald-100 text-emerald-800" : day.is_week_off ? "bg-slate-200 text-slate-600" : "bg-[#0D1B2A]/[0.04] text-[#0D1B2A]"}`}>{day.day}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(reportData?.employees || []).map((employee, rowIndex) => (
                  <tr key={employee.employee_ref_id} data-testid={`muster-report-row-${rowIndex}`}>
                    <td className="sticky left-0 z-10 min-w-[170px] border-b border-r border-[#0D1B2A]/10 bg-white px-3 py-2 font-semibold text-[#0D1B2A]">
                      {employee.employee_name}
                      <span className="block text-[10px] font-normal text-[#0D1B2A]/45">{employee.employee_id}</span>
                    </td>
                    {[employee.present_days, employee.absent_days, employee.leave_days, employee.holiday_days, employee.week_off_days].map((value, valueIndex) => (
                      <td key={valueIndex} className="border-b border-l border-[#0D1B2A]/10 bg-[#F4A300]/[0.06] px-2 py-2 text-center font-bold text-[#0D1B2A]">{value}</td>
                    ))}
                    {employee.days.map((day) => (
                      <td key={day.date} className="border-b border-l border-[#0D1B2A]/10 px-0.5 py-1 text-center">
                        <span className={`inline-flex min-w-[22px] justify-center rounded-md border px-1 py-0.5 text-[10px] font-bold ${codeStyles[day.code] || codeStyles["-"]}`}>{day.code}</span>
                      </td>
                    ))}
                  </tr>
                ))}
                {!reportData?.employees?.length ? (
                  <tr><td colSpan={6 + dates.length} className="py-10 text-center text-sm text-[#0D1B2A]/50">No employees for this selection.</td></tr>
                ) : null}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderReportBody = () => {
    if (loading) {
      return <div className="rounded-3xl border border-[#0D1B2A]/10 bg-white px-8 py-16 text-center text-sm font-semibold text-[#0D1B2A]/60" data-testid="report-loading">Generating report…</div>;
    }
    if (!reportData) return null;
    if (activeReport.id === "daily-attendance") return renderDaily();
    if (activeReport.id === "monthly-attendance") return renderMonthly();
    if (activeReport.id === "lop") return renderLop();
    if (activeReport.id === "muster-roll") return renderMuster();
    return null;
  };

  const renderReportView = () => (
    <div className="space-y-6">
      <Card className="border-[#0D1B2A]/10 bg-white" data-testid="report-filter-panel">
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-xl font-bold text-[#0D1B2A]" data-testid="active-report-title">{activeReport.name}</CardTitle>
            <CardDescription className="text-sm text-[#0D1B2A]/60">{activeReport.description}</CardDescription>
          </div>
          <Button type="button" variant="outline" className="rounded-full" onClick={closeReport} data-testid="report-back-button">
            <ArrowLeft className="mr-2 h-4 w-4" /> All Reports
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {renderDateControls()}
            {renderCommonFilters()}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button type="button" className="rounded-full bg-[#F4A300] px-6 text-[#0D1B2A] hover:bg-[#d98f00]" onClick={() => loadReport(activeReport)} data-testid="report-generate-button">
              <Search className="mr-2 h-4 w-4" /> Generate
            </Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={resetFilters} data-testid="report-reset-button">
              <RotateCcw className="mr-2 h-4 w-4" /> Reset Filters
            </Button>
            <div className="ml-auto flex gap-3">
              <Button type="button" variant="outline" className="rounded-full border-[#0D7A5F]/30 text-[#0D7A5F] hover:bg-[#0D7A5F]/10" disabled={!!exporting} onClick={() => exportReport("xlsx")} data-testid="report-export-xlsx-button">
                <FileSpreadsheet className="mr-2 h-4 w-4" /> {exporting === "xlsx" ? "Exporting…" : "Excel"}
              </Button>
              <Button type="button" variant="outline" className="rounded-full" disabled={!!exporting} onClick={() => exportReport("csv")} data-testid="report-export-csv-button">
                <FileText className="mr-2 h-4 w-4" /> {exporting === "csv" ? "Exporting…" : "CSV"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {renderSummaryCards()}
      {renderReportBody()}
    </div>
  );

  const headerActions = !activeReport ? (
    <div className="flex items-center gap-2 rounded-full border border-[#0D1B2A]/10 bg-white px-4 py-2 text-xs font-semibold text-[#0D1B2A]/60" data-testid="reports-count-pill">
      <Download className="h-4 w-4 text-[#F4A300]" /> {catalog.length} reports available
    </div>
  ) : null;

  return (
    <AppShell
      title="Reports"
      subtitle="Operational attendance registers, working-hour summaries, and loss-of-pay insights — filtered and export-ready."
      navItems={navItems}
      user={user}
      tenant={tenant}
      onLogout={logout}
      actions={headerActions}
    >
      <div data-testid="reports-module-root">{activeReport ? renderReportView() : renderLanding()}</div>
    </AppShell>
  );
}
